import CM from 'codemirror';
import React from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';

import Results from './Results';

// Load CodeMirror additions.
import 'codemirror/addon/lint/lint';
import 'codemirror/mode/css/css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/php/php';

// Load CSS.
import 'bootstrap/dist/css/bootstrap.css';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/neat.css';
import 'codemirror/addon/lint/lint.css';
import './App.css';

const CHECK_ENDPOINT = 'https://v82n1xykt2.execute-api.us-east-1.amazonaws.com/Prod/check';

export default class App extends React.Component {
	state = {
		// Inputs.
		filename: '',
		type: 'php',
		code: '',
		version: 'latest',

		// API results.
		error: null,
		result: '',
		loading: false,

		// CodeMirror state.
		annotations: [],
	}
	cm = null;
	serializeTimer = null;
	updateCmAnnotations = null;

	componentDidMount() {
		this.restoreState();
	}

	restoreState() {
		try {
			if ( window.location.hash ) {
				const decoded = atob( window.location.hash.substr( 1 ) );
				const data = JSON.parse( decoded );
				this.setState( {
					filename: data.filename || '',
					type: data.type || 'php',
					code: data.code || '',
					version: data.version || 'latest',
				} );
			}
		} catch ( err ) {
			return;
		}
	}

	serializeState = () => {
		// Serialize state.
		const state = {
			filename: this.state.filename,
			type: this.state.filename ? null : this.state.type,
			code: this.state.code,
			version: this.state.version,
		};
		const encoded = btoa( JSON.stringify( state ) );

		window.location.hash = encoded;
	}

	onChangeField( key, value ) {
		this.setState( {
			[ key ]: value,
		} );
		if ( key === 'code' ) {
			// Clear annotations too.
			this.setState( {
				annotations: [],
			} );
		}

		// Serialize, but debounced.
		if ( this.serializeTimer ) {
			clearTimeout( this.serializeTimer );
		}
		this.serializeTimer = setTimeout( this.serializeState, 500 );
	}

	onSubmit = async ( e = null ) => {
		if ( e ) {
			e.preventDefault();
		}

		this.setState( {
			error: null,
			result: '',
			loading: true,
		} );

		const data = new URLSearchParams( {
			filename: this.state.filename,
			type: this.state.type,
			code: this.state.code,
			version: this.state.version,
		} );

		const opts = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: data,
		};

		try {
			const res = await fetch( CHECK_ENDPOINT, opts );
			const responseData = await res.json();
			if ( responseData.error ) {
				throw new Error( responseData.message );
			}
			this.setState( {
				result: responseData,
				loading: false,
			} );
			this.updateAnnotations( responseData );
		} catch ( err ) {
			this.setState( {
				error: err,
				loading: false,
			} );
		}
	}

	getAnnotations = ( code, updateAnnotations ) => {
		this.updateCmAnnotations = updateAnnotations;
		if ( this.state.annotations ) {
			updateAnnotations( this.state.annotations );
		}
	}

	updateAnnotations = result => {
		const annotations = [];
		Object.keys( result ).forEach( filename => {
			result[ filename ].forEach( item => {
				annotations.push( {
					message: item.message,
					severity: item.severity,

					// Highlight the entire line. (GH doesn't show per-column
					// annotations, so we copy that here. Plus, phpcs' columns
					// are wrong, as it converts tabs to spaces.)
					from: CM.Pos( item.line - 1, 0 ),
					to: CM.Pos( item.line, 0 ),
				} );
			} );
		} );
		this.setState( {
			annotations,
		} );
		this.updateCmAnnotations( annotations );
	}

	onFocusLine = line => {
		this.cm.setCursor( line - 1, 0 );
		this.cm.focus();
	}

	render() {
		const hasFilename = Boolean( this.state.filename );
		const fnParts = hasFilename && this.state.filename.split( '.' );
		const type = hasFilename ? fnParts[ fnParts.length - 1 ] : this.state.type;
		let mode;
		switch ( type ) {
			case 'php':
			case 'css':
				mode = type;
				break;

			case 'js':
				mode = 'javascript';
				break;

			default:
				mode = 'text';
				break;
		}

		const codemirrorOpts = {
			lineNumbers: true,
			mode,
			tabSize: 4,
			indentUnit: 4,
			indentWithTabs: true,
			theme: 'neat',
			gutters: [
				'CodeMirror-lint-markers',
			],
			lint: {
				getAnnotations: this.getAnnotations,
				async: true,
			},
			extraKeys: {
				"Cmd-Enter": () => {
					this.onSubmit();
				},
				"Cmd-S": () => {
					this.onSubmit();
				},
			},
		};
		return (
			<div className="App container-fluid px-0 d-flex">
				<div className="App__wrap row align-self-stretch mx-0 flex-grow-1">
					<form
						className="col-md-6 my-3 d-flex flex-column"
						onSubmit={ this.onSubmit }
					>
						<div className="form-row form-group flex-shrink-0">
							<div className="col">
								<input
									className="form-control"
									placeholder="Filename including extension…"
									name="filename"
									type="text"
									value={ this.state.filename }
									onChange={ e => this.onChangeField( 'filename', e.target.value ) }
								/>
							</div>
							<div className="col">
								<select
									className="form-control"
									disabled={ hasFilename }
									name="type"
									value={ hasFilename ? 'auto' : this.state.type }
									onChange={ e => this.onChangeField( 'type', e.target.value ) }
								>
									{ hasFilename ? (
										<option value="auto">Automatic from filename</option>
									) : (
										<>
											<option value="php">PHP</option>
											<option value="js">JS</option>
											<option value="css">CSS</option>
										</>
									) }
								</select>
							</div>
						</div>
						<div className="form-group flex-grow-1 d-flex flex-column">
							<CodeMirror
								className="flex-grow-1 d-flex flex-column"
								cursor={ this.state.cursor }
								value={ this.state.code }
								options={ codemirrorOpts }
								editorDidMount={ editor => {
									this.cm = editor;
								} }
								onBeforeChange={ ( editor, data, value ) => {
									this.onChangeField( 'code', value );
								} }
							/>
						</div>
						<div className="form-row">
							<div className="col">
								{ this.state.loading ? (
									<button
										className="btn btn-primary"
										disabled
										type="button"
									>
										<span
											className="spinner-border spinner-border-sm"
											role="status"
											aria-hidden="true"
										/>
										{ ' ' }
										Checking…
									</button>
								) : (
									<button
										className="btn btn-primary"
										disabled={ this.state.loading }
										type="submit"
									>
										Check code
									</button>
								) }
							</div>

							<div className="col form-inline">
								<label
									className="mr-1"
									htmlFor="version"
								>
									Version:
								</label>
								<select
									className="form-control flex-grow-1"
									id="version"
									value={ this.state.version }
									onChange={ e => this.onChangeField( 'version', e.target.value ) }
								>
									<option>latest</option>
									<option>0.8.0</option>
									<option>0.7.0</option>
									<option>0.6.0</option>
									<option>0.5.0</option>
									<option>0.4.2</option>
								</select>
							</div>
						</div>
					</form>
					<Results
						error={ this.state.error }
						loading={ this.state.loading }
						result={ this.state.result }
						onFocusLine={ this.onFocusLine }
					/>
				</div>
			</div>
		);
	}
}
